import React, { useEffect, useMemo, useRef } from "react";
import * as ChartJS from "chart.js";
import {
  Book,
  ArrowClockwise,
  ExclamationTriangle,
} from "react-bootstrap-icons";
import ChartDataLabels from "chartjs-plugin-datalabels";
import styles from "./Syllabuscompletion.module.css";

ChartJS.Chart.register(
  ChartJS.BarController,
  ChartJS.BarElement,
  ChartJS.CategoryScale,
  ChartJS.LinearScale,
  ChartJS.Tooltip,
  ChartJS.Legend,
);

/* ============================================================================
 * Defaults
 * ==========================================================================*/
const DEFAULT_COLORS = {
  overall: { from: "#6366f1", to: "#a5b4fc" }, // violet (matches --gradient-violet-indigo)
  month: { from: "#10b981", to: "#6ee7b7" }, // teal/green (matches --gradient-green-pair)
};

const DEFAULT_LABELS = {
  overall: "Page wise completion",
  month: "Monthly completion",
};

/* Build a CanvasGradient Chart.js can paint each bar with. */
const makeBarGradient = (chart, [from, to]) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) return from;
  const g = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
};

/* Normalise a height prop to a valid CSS value.
 * 200 / "200" → "200px"; "50%" / "200px" → unchanged. */
const toCssHeight = (h) => (/^\d+$/.test(String(h)) ? `${h}px` : h);

/* ============================================================================
 * State sub-components
 * ==========================================================================*/
const SkeletonState = () => (
  <div className={styles.skeletonBody}>
    {[...Array(5)].map((_, i) => (
      <div key={i} className={styles.skeletonRow} />
    ))}
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className={styles.errorState}>
    <ExclamationTriangle size={40} className={styles.errorIcon} />
    <p className={styles.errorText}>{message}</p>
    {onRetry && (
      <button type="button" onClick={onRetry} className={styles.retryBtn}>
        <ArrowClockwise size={16} /> Retry
      </button>
    )}
  </div>
);

const EmptyState = ({ message }) => (
  <div className={styles.emptyState}>{message}</div>
);

/* ============================================================================
 * MAIN: SyllabusCompletion
 *
 * Props:
 *   data             → [{ id, subject, staff, overall, month }]   (UI-shape array)
 *   status           → "idle" | "loading" | "success" | "error"
 *   title            → header title
 *   note             → optional caption shown at the TOP (below the header)
 *   labels           → { overall, month } legend/series text
 *   colors           → { overall: {from, to}, month: {from, to} }
 *   height           → chart height (number/px/%) (default 380)
 *   maxLeft          → diverging X-axis left bound % (default 100)
 *   maxRight         → diverging X-axis right bound % (default 100)
 *   onRetry          → () => void
 *   emptyMessage     → text when status==="success" with no rows
 *   errorMessage     → text shown above the retry button
 *   alignChartBottom → when true, the card fills its parent height and the
 *                      chart is pushed to the BOTTOM (note stays at the top).
 *                      Used for the subject-teacher card where a single
 *                      subject would otherwise float at the top.
 * ==========================================================================*/
const SyllabusCompletion = ({
  data = [],
  status = "loading",
  title = "Syllabus Completion",
  note,
  labels = DEFAULT_LABELS,
  colors = DEFAULT_COLORS,
  height = 380,
  maxLeft = 100,
  maxRight = 100,
  xAxisLabel = "Percentage",
  onRetry,
  emptyMessage = "No syllabus data available.",
  errorMessage = "Something went wrong while loading syllabus completion.",
  alignChartBottom = false,
  top,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const wrapHeight = toCssHeight(height);

  // Sort subjects by overall % so the chart reads top-to-bottom worst → best
  // (matches the screenshot's English → Social Studies progression)
  const sortedRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return [...data].sort((a, b) => (a.overall ?? 0) - (b.overall ?? 0));
  }, [data]);

  /* Draws each value just past the bar's tip, on the OUTER side.
   * Deterministic: uses the bar element's own tip pixel (bar.x) so it never
   * lands inside the bar, regardless of value size or sign. */
  const outsideValueLabels = {
    id: "outsideValueLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      ctx.save();
      ctx.font = "600 12px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.fillStyle = "#000";
      ctx.textBaseline = "middle";

      chart.data.datasets.forEach((ds, di) => {
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden) return;
        meta.data.forEach((bar, i) => {
          const val = Math.abs(Math.round(ds.data[i]));
          if (!val) return; // skip 0% rows
          const isLeft = ds.data[i] < 0; // overall = negated → left side
          if (isLeft) {
            ctx.textAlign = "right";
            ctx.fillText(`${val}%`, bar.x - 6, bar.y); // 6px left of the tip
          } else {
            ctx.textAlign = "left";
            ctx.fillText(`${val}%`, bar.x + 6, bar.y); // 6px right of the tip
          }
        });
      });
      ctx.restore();
    },
  };

  useEffect(() => {
    if (status !== "success" || sortedRows.length === 0) return;
    if (!canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    // Diverging trick: negate the "overall" values so they grow LEFT from 0
    const overallNegated = sortedRows.map((r) => -Math.abs(r.overall ?? 0));
    const monthValues = sortedRows.map((r) => r.month ?? 0);
    const subjectLabels = sortedRows.map((r) => r.subject || "");

    chartRef.current = new ChartJS.Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: subjectLabels,
        datasets: [
          {
            label: labels.overall,
            data: overallNegated,
            backgroundColor: (ctx) =>
              makeBarGradient(ctx.chart, [
                colors.overall.from,
                colors.overall.to,
              ]),
            borderWidth: 0,
            borderRadius: 6,
            categoryPercentage: 0.85,
            barPercentage: 1,
            // OVERALL dataset
            datalabels: {
              anchor: "end", // left tip of the bar
              align: "left", // ALWAYS push further left (outer side) ✓
              offset: 6,
              formatter: (value) => {
                const abs = Math.abs(Math.round(value));
                return abs > 0 ? `${abs}%` : "";
              },
              color: "#000",
              font: { size: 12, weight: "600" },
              clamp: false,
              clip: false,
            },
          },
          {
            label: labels.month,
            data: monthValues,
            backgroundColor: (ctx) =>
              makeBarGradient(ctx.chart, [colors.month.from, colors.month.to]),
            borderWidth: 0,
            borderRadius: 6,
            categoryPercentage: 0.85,
            barPercentage: 1,
            // MONTH dataset
            datalabels: {
              anchor: "end", // right tip of the bar
              align: "right", // ALWAYS push further right (outer side) ✓
              offset: 6,
              formatter: (value) => {
                const v = Math.round(value);
                return v > 0 ? `${v}%` : "";
              },
              color: "#000",
              font: { size: 12, weight: "600" },
              clamp: false,
              clip: false,
            },
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { left: 40, right: 40, top: 8, bottom: 4 } },
        plugins: {
          legend: {
            position: "bottom",
            align: "center",
            labels: {
              boxWidth: 12,
              boxHeight: 12,
              padding: 15,
              usePointStyle: false,
              color: "#334155",
              font: { size: 13, weight: "500" },
            },
          },
          tooltip: {
            backgroundColor: "#1E293B",
            titleColor: "#fff",
            bodyColor: "#fff",
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              title: (items) => {
                const i = items?.[0]?.dataIndex ?? 0;
                const row = sortedRows[i];
                return `${row.subject}${row.staff ? ` — ${row.staff}` : ""}`;
              },
              label: (ctx) => {
                // Show absolute value (overall was negated for the diverging effect)
                const raw = ctx.parsed.x;
                const value = Math.abs(Math.round(raw));
                const seriesLabel = ctx.dataset.label;
                return ` ${seriesLabel}: ${value}%`;
              },
              afterLabel: (ctx) => {
                const row = sortedRows[ctx.dataIndex];
                if (!row) return "";
                // dataset 0 = overall ("Complete %"), dataset 1 = month ("Month %")
                const isMonth = ctx.datasetIndex === 1;
                const total = isMonth
                  ? (row.subTotalPages ?? 0)
                  : (row.totalPages ?? 0);
                const completed = isMonth
                  ? (row.monthCompletedPages ?? 0)
                  : (row.overallCompletedPages ?? 0);
                return [
                  `Total Pages: ${total}`,
                  `Completed Pages: ${completed}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            min: -maxLeft,
            max: maxRight,
            ticks: {
              color: "#64748B",
              font: { size: 12 },
              stepSize: 20,
              callback: (v) => `${Math.abs(v)}%`,
            },
            title: {                  // <--- ADD THIS BLOCK
              display: true,
              text: xAxisLabel,       // Uses your prop value dynamically
              color: "#64748B",       // Matches your tick colors
              font: {
                size: 13,
                weight: "600"
              },
              padding: { top: 10 }    // Adds breathing room below the ticks
            },
            grid: {
              color: "#e2e8f0",
              drawBorder: false,
              drawTicks: false,
              drawOnChartArea: true,
            },
            border: { display: false },
          },
          y: {
            stacked: true,
            ticks: {
              color: "#1E293B",
              font: { size: 13, weight: "500" },
              padding: 8,
            },
            grid: { display: false },
            border: { display: false },
          },
        },
      },
      plugins: [outsideValueLabels],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [sortedRows, status, labels, colors, maxLeft, maxRight]);

  return (
    <div
      className={styles.card}
      style={
        alignChartBottom
          ? { display: "flex", flexDirection: "column", height: "100%" }
          : undefined
      }
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon} aria-hidden="true">
            <Book size={18} />
          </span>
          <h2 className={styles.title}>{title}</h2>
        </div>
      </div>

      {/* Note — rendered at the TOP, just below the header. */}
      {note && (
        <div className={styles.noteBanner}>
          <strong>Note:</strong> {note}
        </div>
      )}

      {(status === "idle" || status === "loading") && <SkeletonState />}
      {status === "error" && (
        <ErrorState message={errorMessage} onRetry={onRetry} />
      )}
      {status === "success" && sortedRows.length === 0 && (
        <EmptyState message={emptyMessage} />
      )}
      {status === "success" && sortedRows.length > 0 && (
        <div
          className={styles.chartWrap}
          style={
            alignChartBottom
              ? { height: wrapHeight, marginTop: "auto" } // push chart to bottom
              : { height: wrapHeight, top: top }
          }
        >
          <canvas ref={canvasRef} />
        </div>
      )}
    </div>
  );
};

export default SyllabusCompletion;
